import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { CorreoConfiguracion } from "@/components/CorreoConfiguracion";
import {
  type FilaBandeja,
  useBandejaCorreo,
  useConfigCorreo,
  useDetalleCorreo,
  useSimsAnunciadas,
  useTableroCorreo,
} from "@/hooks/useCorreo";
import { cn } from "@/lib/utils";

const DESENLACES: Record<string, string> = {
  consulta: "Consulta",
  aviso_falla: "Aviso de falla",
  pedido_srp: "Pedido de SRP",
  protrans: "Protrans",
  asignacion_sim: "Asignación de SIM",
  otro: "Desconocido",
  fuera_de_lista: "Fuera de lista blanca",
  por_revisar: "Por revisar",
  lista_negra: "Basura",
  solo_registro: "Sólo registrado",
  descartado: "Descartado",
  automatico: "Automático",
  sin_analizar: "Sin analizar",
};
const ESTADOS: Record<string, string> = {
  pending: "pendiente",
  approved: "aprobado",
  rejected: "rechazado",
  expired: "expirado",
  enviado: "enviado",
  envio_apagado: "aprobado (envío apagado)",
};

const cuando = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "America/Mexico_City",
      })
    : "—";
const usd = (v: number | null | undefined) =>
  v == null ? "—" : `USD ${v.toFixed(4)}`;
const mxn = (n: number) => `MXN $${n.toFixed(2)}`;
const minutos = (s: number | null) =>
  s == null
    ? "—"
    : s < 120
      ? `${Math.round(s)} s`
      : `${Math.round(s / 60)} min`;
const etiqueta = (d: string) => DESENLACES[d] ?? d;
const estado = (e: string | null) => (e ? (ESTADOS[e] ?? e) : "—");

const MESES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/**
 * El gasto se cobra en dólares; se muestra en pesos con el FIX de Banxico
 * vigente (o el último publicado, y se dice), y el dólar en pequeño debajo.
 * Sin tipo de cambio, en dólares y dicho: nunca un peso inventado.
 */
function enPesos(
  usoUsd: number,
  topeUsd: number,
  tc:
    | { rate: number; fuente: string; fecha: string | null; es_de_hoy: boolean }
    | null
    | undefined,
) {
  const dolares = `USD ${usoUsd.toFixed(2)}${topeUsd > 0 ? ` de USD ${topeUsd.toFixed(2)}` : ""}`;
  if (!tc) {
    return {
      uso: usoUsd,
      tope: topeUsd,
      fmt: (n: number) => `USD ${n.toFixed(2)}`,
      pie: "Sin tipo de cambio cargado: se muestra en dólares.",
    };
  }
  const [, m, dd] = (tc.fecha ?? "").split("-");
  const fecha = tc.fecha ? `${dd}-${MESES[Number(m) - 1]}` : "";
  const fuente = tc.fuente === "banxico" ? "TC FIX" : "TC manual";
  return {
    uso: usoUsd * tc.rate,
    tope: topeUsd * tc.rate,
    fmt: mxn,
    pie: `≈ ${dolares} · ${fuente} ${tc.rate.toFixed(4)} del ${fecha}${
      tc.es_de_hoy ? "" : " (último disponible)"
    }`,
  };
}

function Medidor({
  titulo,
  uso,
  tope,
  fmt,
  pie,
}: {
  titulo: string;
  uso: number;
  tope: number;
  fmt: (n: number) => string;
  pie?: string;
}) {
  const pct = tope > 0 ? Math.min(100, (uso / tope) * 100) : 0;
  return (
    <div className="rounded-lg border bg-card p-3 text-sm">
      <div className="text-muted-foreground">{titulo}</div>
      <div className="font-semibold">
        {fmt(uso)}{" "}
        <span className="font-normal text-muted-foreground">
          / {tope > 0 ? fmt(tope) : "sin tope"}
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded bg-muted">
        <div
          className={cn(
            "h-1.5 rounded",
            pct >= 90 ? "bg-red-500" : "bg-primary",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {pie && <div className="mt-1 text-xs text-muted-foreground">{pie}</div>}
    </div>
  );
}

function Tablero() {
  const [dias, setDias] = useState(14);
  const t = useTableroCorreo(dias);
  if (t.isPending) return <Loader2 className="size-5 animate-spin" />;
  if (t.isError || !t.data)
    return (
      <div className="text-sm text-destructive">
        No se pudo leer el tablero.
      </div>
    );
  const d = t.data;
  const porCaso: Record<string, number> = {};
  const porDia: Record<string, number> = {};
  for (const f of d.por_dia) {
    porCaso[f.desenlace] = (porCaso[f.desenlace] ?? 0) + f.n;
    porDia[f.dia] = (porDia[f.dia] ?? 0) + f.n;
  }
  const maxDia = Math.max(1, ...Object.values(porDia));
  const b = d.buzon;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        Últimos
        <select
          className="rounded border bg-background px-2 py-1"
          value={dias}
          onChange={(e) => setDias(Number(e.target.value))}
        >
          {[7, 14, 30, 90].map((n) => (
            <option key={n} value={n}>
              {n} días
            </option>
          ))}
        </select>
      </div>

      <section className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border bg-card p-3 text-sm">
          <h2 className="mb-2 font-medium">Recibidos por día</h2>
          {Object.keys(porDia).length === 0 && (
            <div className="text-muted-foreground">
              Sin correos en el periodo.
            </div>
          )}
          {Object.entries(porDia).map(([dia, n]) => (
            <div key={dia} className="flex items-center gap-2">
              <span className="w-24 tabular-nums text-muted-foreground">
                {dia}
              </span>
              <div
                className="h-3 rounded bg-primary"
                style={{ width: `${(n / maxDia) * 70}%` }}
              />
              <span className="tabular-nums">{n}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border bg-card p-3 text-sm">
          <h2 className="mb-2 font-medium">Por caso</h2>
          {Object.entries(porCaso).map(([k, n]) => (
            <div key={k} className="flex justify-between">
              <span>{etiqueta(k)}</span>
              <span className="tabular-nums">{n}</span>
            </div>
          ))}
          <h2 className="mb-1 mt-3 font-medium">Borradores</h2>
          {["pending", "approved", "rejected", "expired", "enviado"].map(
            (k) => (
              <div key={k} className="flex justify-between">
                <span>{estado(k)}</span>
                <span className="tabular-nums">{d.borradores[k] ?? 0}</span>
              </div>
            ),
          )}
          <div className="mt-3 text-muted-foreground">
            Lectura → borrador: <b>{minutos(d.mediana_lectura_a_borrador_s)}</b>{" "}
            · borrador → decisión:{" "}
            <b>{minutos(d.mediana_borrador_a_decision_s)}</b> (medianas)
          </div>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-4">
        <Medidor
          titulo="Tokens hoy"
          uso={d.gasto.tokens_dia}
          tope={d.gasto.tope_tokens_dia}
          fmt={(n) => n.toLocaleString("es-MX")}
        />
        <Medidor
          titulo="Tokens del mes"
          uso={d.gasto.tokens_mes}
          tope={d.gasto.tope_tokens_mes}
          fmt={(n) => n.toLocaleString("es-MX")}
        />
        <Medidor
          titulo="Gasto hoy"
          {...enPesos(d.gasto.usd_dia, d.gasto.tope_usd_dia, d.tipo_cambio)}
        />
        <Medidor
          titulo="Gasto del mes"
          {...enPesos(d.gasto.usd_mes, d.gasto.tope_usd_mes, d.tipo_cambio)}
        />
      </section>

      <section className="rounded-lg border bg-card p-3 text-sm">
        <h2 className="mb-2 font-medium">Estado del buzón</h2>
        <dl className="grid grid-cols-[12rem_1fr] gap-y-1">
          <dt className="text-muted-foreground">Interpretación</dt>
          <dd>{b.interpretar ? "encendida" : "apagada"}</dd>
          <dt className="text-muted-foreground">Último sondeo correcto</dt>
          <dd>{cuando(b.ultimo_ok as string)}</dd>
          <dt className="text-muted-foreground">Último error</dt>
          <dd>
            {b.ultimo_error
              ? `${b.ultimo_error} (${cuando(b.ultimo_error_at as string)}) · ${b.errores ?? 0} en total`
              : "ninguno"}
          </dd>
          <dt className="text-muted-foreground">Alcances concedidos</dt>
          <dd>{(b.alcances as string) || "—"}</dd>
          <dt className="text-muted-foreground">Envío</dt>
          <dd>
            {b.envio === "true"
              ? `encendido (casos: ${b.envio_casos || "ninguno"})`
              : "apagado"}
          </dd>
        </dl>
        {!b.ultimo_sondeo && (
          <div className="mt-2 text-muted-foreground">
            El buzón todavía no ha reportado su estado.
          </div>
        )}
      </section>
    </div>
  );
}

function Detalle({ id }: { id: string }) {
  const q = useDetalleCorreo(id);
  if (q.isPending) return <Loader2 className="size-4 animate-spin" />;
  if (!q.data) return null;
  const r = q.data.registro;
  return (
    <div className="space-y-3 border-t bg-muted/30 p-3 text-sm">
      {r.alerta && (
        <div className="text-red-700 dark:text-red-300">
          Posible manipulación: {r.alerta}
        </div>
      )}
      {r.resumen && <div>{r.resumen}</div>}
      <div>
        <div className="mb-1 font-medium">Correo recibido</div>
        {/* Texto plano: React escapa el contenido; nunca se interpreta como HTML. */}
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded border bg-background p-2 text-xs">
          {String(r.cuerpo || "(vacío)")}
        </pre>
      </div>
      {q.data.aprobaciones.length === 0 && (
        <div className="text-muted-foreground">No generó aprobaciones.</div>
      )}
      {q.data.aprobaciones.map((a) => (
        <div key={a.id} className="rounded border bg-background p-2">
          <div className="flex flex-wrap justify-between gap-2">
            <span className="font-medium">
              {a.tipo === "correo_respuesta"
                ? "Borrador de respuesta"
                : a.tipo === "correo_escalamiento"
                  ? `Escalamiento a ${a.responsable ?? "—"}`
                  : `SRP ${a.playbook_id}`}
            </span>
            <span>
              {estado(a.envio ?? a.estado)} ·{" "}
              <Link className="underline" to="/approvals">
                ver en Aprobaciones
              </Link>
            </span>
          </div>
          {a.evidencia && a.evidencia.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {a.evidencia.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
          {a.para && (
            <div className="text-xs text-muted-foreground">
              Para: {a.para.join(", ") || "(sin destinatario)"}
            </div>
          )}
          {(a.borrador || a.reason) && (
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap text-xs">
              {a.borrador || a.reason}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

function Bandeja() {
  const q = useBandejaCorreo();
  const [abierto, setAbierto] = useState<string | null>(null);
  if (q.isPending) return <Loader2 className="size-5 animate-spin" />;
  const filas = q.data ?? [];
  if (filas.length === 0)
    return (
      <div className="text-sm text-muted-foreground">
        Todavía no hay correos atendidos.
      </div>
    );
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            {[
              "Recibido",
              "Remitente",
              "Asunto",
              "Caso",
              "Veredicto",
              "Costo",
              "Estado",
            ].map((h) => (
              <th key={h} className="px-3 py-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f: FilaBandeja) => (
            <FilaCorreo
              key={f.id}
              f={f}
              abierta={abierto === f.id}
              onClick={() => setAbierto(abierto === f.id ? null : f.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilaCorreo({
  f,
  abierta,
  onClick,
}: {
  f: FilaBandeja;
  abierta: boolean;
  onClick: () => void;
}) {
  return (
    <>
      <tr
        className="cursor-pointer border-t hover:bg-muted/30"
        onClick={onClick}
      >
        <td className="whitespace-nowrap px-3 py-2">{cuando(f.recibido_at)}</td>
        <td className="px-3 py-2">{f.de}</td>
        <td className="px-3 py-2">{f.asunto}</td>
        <td className="px-3 py-2">{etiqueta(f.desenlace)}</td>
        <td className="px-3 py-2">{f.veredicto ?? "—"}</td>
        <td className="whitespace-nowrap px-3 py-2">
          {f.tokens ? `${usd(f.costo_usd)} · ${f.tokens} tk` : "—"}
        </td>
        <td className="px-3 py-2">{estado(f.estado)}</td>
      </tr>
      {abierta && (
        <tr>
          <td colSpan={7}>
            <Detalle id={f.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function Configuracion() {
  const q = useConfigCorreo();
  if (q.isPending) return <Loader2 className="size-5 animate-spin" />;
  if (!q.data)
    return (
      <div className="text-sm text-destructive">
        No se pudo leer la configuración.
      </div>
    );
  return <CorreoConfiguracion c={q.data} />;
}

const ESTADOS_SIM: Record<string, string> = {
  anunciada: "Anunciada (esperando instalación/lectura)",
  verificada: "Verificada en el módem",
  no_coincide: "No coincide",
  apn_incorrecta: "SIM puesta, APN incorrecta",
  alta_pendiente: "Agencia no existe en AROC (alta pendiente)",
  revision_manual: "Revisión manual",
};

/** SIMs anunciadas por correo y lo que dijo el módem. Sólo lectura. */
function Sims() {
  const q = useSimsAnunciadas();
  if (q.isPending) return <Loader2 className="size-5 animate-spin" />;
  if (!q.data?.length)
    return (
      <div className="text-sm text-muted-foreground">
        Ningún correo ha anunciado una SIM todavía.
      </div>
    );
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs text-muted-foreground">
        <tr>
          <th>ICCID</th>
          <th>Operador</th>
          <th>Agencia</th>
          <th>Instalación</th>
          <th>Estado</th>
          <th>Última lectura</th>
        </tr>
      </thead>
      <tbody>
        {q.data.map((s) => (
          <tr key={s.id} className="border-t align-top">
            <td className="py-1">
              <code>{s.iccid ?? "—"}</code>
            </td>
            <td>{s.operador ?? "—"}</td>
            <td>{s.host ?? s.agencia_texto ?? "—"}</td>
            <td>{s.fecha_instalacion ?? "desde ya"}</td>
            <td>
              {ESTADOS_SIM[s.estado] ?? s.estado}
              {s.motivo && (
                <div className="text-xs text-muted-foreground">{s.motivo}</div>
              )}
              {s.aprobacion_id && (
                <div className="text-xs">propuesta en Aprobaciones</div>
              )}
            </td>
            <td className="text-xs">{cuando(s.ultima_lectura_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const PESTANAS = {
  tablero: "Tablero",
  bandeja: "Bandeja",
  sims: "SIMs anunciadas",
  configuracion: "Configuración",
} as const;

/** Correo aroc@tilatina.com: todo lo del buzón de AROC en un lugar. */
export function CorreoPage() {
  const [p, setP] = useState<keyof typeof PESTANAS>("tablero");
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Correo aroc@tilatina.com</h1>
        <p className="text-sm text-muted-foreground">
          Lo que llega al buzón de AROC, cómo lo interpretó y qué propuso. Las
          respuestas se aprueban en Aprobaciones.
        </p>
      </div>
      <div className="flex gap-1 border-b">
        {(Object.keys(PESTANAS) as (keyof typeof PESTANAS)[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setP(k)}
            className={cn(
              "-mb-px border-b-2 px-3 py-1.5 text-sm",
              p === k
                ? "border-primary font-medium"
                : "border-transparent text-muted-foreground",
            )}
          >
            {PESTANAS[k]}
          </button>
        ))}
      </div>
      {p === "tablero" && <Tablero />}
      {p === "bandeja" && <Bandeja />}
      {p === "sims" && <Sims />}
      {p === "configuracion" && <Configuracion />}
    </div>
  );
}
